import styled from '@emotion/styled'
import { MutableRefObject, forwardRef } from 'react'

import { Flex } from './Flex'
import { Icon, IconDesign, IconName } from './Icon'
import { Link } from './Link'
import { Loader } from './Loader'
import { BUTTON_SIZE_MAP, Color, buttonStyles, theme } from '../constants'
import { Intent } from '../constants/intent'
import { StyleProps, createStyles } from '../utils'

type ButtonColors = {
  backgroundColor: string | undefined
  color: string | undefined
  filter?: string
  hoverBackgroundColor: string | undefined
  hoverColor: string | undefined
}

type ButtonComponentProps = {
  border?: 'none'
  design?: ButtonDesign
  disabled?: boolean
  fullWidth?: boolean
  icon?: IconName
  iconDesign?: IconDesign
  loading?: boolean
  nowrap?: boolean
  rounded?: boolean
  size?: ButtonSize
  styles?: StyleProps
  text?: boolean
  title?: string
  openNewTab?: boolean
  to?: string
}

type ButtonDesign =
  | Intent
  | 'cta'
  | 'danger-inverted'
  | 'dark-green'
  | 'dark-green-inverted'
  | 'danger'
  | 'danger-inverted'
  | 'primary'
  | 'primary-inverted'
  | 'inverted'
  | 'light-grey'
  | 'white'
  | 'transparent-light'

type ButtonProps = JSX.IntrinsicElements['button'] &
  ButtonComponentProps & {
    openNewTab?: boolean
    to?: string
    ref?: MutableRefObject<HTMLButtonElement | null>
  }

type ButtonSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

const BUTTON_DISABLED_COLOR = Color.DARK_GREY

const getColors = ({ design, text }: ButtonComponentProps): ButtonColors => {
  const colors = getColorsByDesign(design)

  if (text) {
    const color = design ? colors.backgroundColor : Color.DARK_GREY
    return {
      backgroundColor: 'transparent',
      color,
      hoverBackgroundColor: 'inherit',
      hoverColor: color,
    }
  }

  return colors
}

const getColorsByDesign = (design?: ButtonDesign) => {
  switch (design) {
    case 'cta':
      return {
        backgroundColor: theme.color.cta,
        color: Color.WHITE,
        hoverBackgroundColor: Color.DARK_GREEN,
        hoverColor: Color.WHITE,
      }
    case 'danger':
      return {
        backgroundColor: Color.RED,
        color: Color.WHITE,
        hoverBackgroundColor: Color.DARK_RED,
        hoverColor: Color.WHITE,
      }
    case 'danger-inverted':
      return {
        backgroundColor: Color.WHITE,
        color: Color.RED,
        hoverBackgroundColor: Color.LIGHTER_RED,
        hoverColor: Color.RED,
      }
    case 'dark-green':
      return {
        backgroundColor: Color.DARK_GREEN,
        color: Color.WHITE,
        hoverBackgroundColor: Color.GREEN,
        hoverColor: Color.WHITE,
      }
    case 'dark-green-inverted':
      return {
        backgroundColor: Color.WHITE,
        color: Color.DARK_GREEN,
        hoverBackgroundColor: Color.LIGHTER_GREEN,
        hoverColor: Color.DARK_GREEN,
      }
    case 'success':
      return {
        backgroundColor: Color.LIGHTER_GREEN,
        color: Color.DARK_GREEN,
        hoverBackgroundColor: Color.GREEN,
        hoverColor: Color.WHITE,
      }
    case 'inverted':
      return {
        backgroundColor: Color.WHITE,
        color: Color.BLUE,
        hoverBackgroundColor: Color.LIGHTEST_BLUE,
        hoverColor: Color.BLUE,
      }
    case 'info':
      return {
        backgroundColor: Color.LIGHTER_BLUE,
        color: Color.DARK_BLUE,
        hoverBackgroundColor: Color.BLUE,
        hoverColor: Color.WHITE,
      }
    case 'light-grey':
      return {
        backgroundColor: Color.LIGHT_GREY,
        color: Color.BLACK,
        hoverBackgroundColor: Color.LEGACY_GRAY,
        hoverColor: Color.BLACK,
      }
    case 'primary-inverted':
      return {
        backgroundColor: Color.WHITE,
        color: Color.BLUE,
        hoverBackgroundColor: Color.LIGHTEST_BLUE,
        hoverColor: Color.BLUE,
      }
    case 'warning':
      return {
        backgroundColor: Color.LIGHTER_RED,
        color: Color.DARK_RED,
        hoverBackgroundColor: Color.LIGHT_RED,
        hoverColor: Color.DARK_RED,
      }
    case 'white':
      return {
        backgroundColor: Color.WHITE,
        color: Color.BLACK,
        hoverBackgroundColor: Color.LIGHTEST_BLUE,
        hoverColor: Color.BLACK,
      }
    case 'transparent-light':
      return {
        backgroundColor: 'transparent',
        color: Color.TRANSPARENT_DARKER_GREY,
        hoverBackgroundColor: Color.TRANSPARENT_GREY,
        hoverColor: Color.BLACK,
      }
    case 'primary':
    default:
      return {
        backgroundColor: theme.color.primary,
        color: Color.WHITE,
        hoverBackgroundColor: Color.DARK_BLUE,
        hoverColor: Color.WHITE,
      }
  }
}

const ButtonElement = styled.button<ButtonComponentProps>((props) => {
  const { design, nowrap, rounded, size, text } = props

  const hasBorder =
    !text &&
    (props.border === 'none' ||
      design === 'white' ||
      design === 'light-grey' ||
      design?.endsWith('-inverted'))

  const disabledProps = {
    backgroundColor: Color.MEDIUM_GREY,
    color: BUTTON_DISABLED_COLOR,
  }
  const dimensions = BUTTON_SIZE_MAP[size ?? 'md']
  const paddingX = text ? 0 : rounded ? 10 : dimensions.paddingX
  const paddingY = rounded ? 6 : dimensions.paddingY

  const designProps = getColors(props)

  return {
    ...buttonStyles(props),
    backgroundColor: designProps.backgroundColor,
    ...(hasBorder ? {} : { border: '1px solid transparent' }),
    ...(rounded ? { borderRadius: 999 } : {}),
    boxShadow: hasBorder ? theme.boxShadow.normal : undefined,
    boxSizing: 'border-box',
    color: designProps.color,
    display: 'inline-block',
    fontWeight: theme.font.weight.bold,
    textAlign: 'center',
    paddingBottom: paddingY,
    paddingLeft: paddingX,
    paddingRight: paddingX,
    paddingTop: paddingY,
    whiteSpace: nowrap ? 'nowrap' : undefined,
    width: props.fullWidth ? '100%' : undefined,
    '&:disabled': {
      ...disabledProps,
      cursor: 'not-allowed',
    },
    '&:hover:not(:disabled)': {
      backgroundColor: designProps.hoverBackgroundColor,
      color: designProps.hoverColor,
      filter: designProps.filter,
    },
    ...createStyles(props.styles),
  }
})

const ContentWrapper = styled.div<{ isLoading?: boolean }>((props) => ({
  visibility: props.isLoading ? 'hidden' : 'visible',
}))

const LoaderWrapper = styled.div({
  left: '50%',
  pointerEvents: 'none',
  position: 'absolute',
  top: '50%',
  transform: 'scale(0.65) translate(-75%, -50%)',
  width: '100%',
  height: '100%',
})

export type { ButtonComponentProps, ButtonDesign, ButtonProps, ButtonSize }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const { children, fullWidth, loading, openNewTab, to, ...buttonProps } =
      props

    const disabled = props.disabled || !!loading
    const iconSize =
      buttonProps.size === 'xxs' ||
      buttonProps.size === 'xs' ||
      buttonProps.size === 'sm'
        ? 'sm'
        : 'md'

    return (
      <ButtonElement
        {...buttonProps}
        as={to && !disabled ? Link : undefined}
        openNewTab={openNewTab}
        to={to}
        ref={ref}
        disabled={disabled}
        fullWidth={fullWidth}
        styles={{
          // Keep the button height consistent with icon-only buttons:
          lineHeight: `${theme.elements.icon.size[iconSize]}px`,
          ...buttonProps.styles,
        }}
      >
        {loading && (
          <LoaderWrapper>
            <Loader color={BUTTON_DISABLED_COLOR} />
          </LoaderWrapper>
        )}
        <ContentWrapper isLoading={!!loading}>
          {props.icon ? (
            <Flex gap={2} items="center" justify="center">
              <Icon
                name={props.icon}
                size={iconSize}
                design={props.iconDesign}
              />
              {(!!children || children === 0) && <div>{children}</div>}
            </Flex>
          ) : (
            children
          )}
        </ContentWrapper>
      </ButtonElement>
    )
  },
)

Button.displayName = 'Button'
